// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  Constants,
  ErrorMessage,
  IApplicationError,
  ParticipantRegistrationModel,
  ParticipantTokenValidationModel,
  ParticipantValidationStages,
  SimpleStringResult,
  TokenValidationErrorTypes
} from '../../../models';
import { AppService } from '../app.service';
import { OidcService } from '../account/oidc.service';
import { ParticipantValidationBaseService } from './participantvalidation-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class ParticipantValidationService extends ParticipantValidationBaseService {
  private participantId: string | null = null;

  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private oidcService: OidcService,
    private router: Router,
    appService: AppService,
  ) {
    super(appService);
  }

  validateParticipantToken(id: string | null, destination: string | null): void {
    if (!id) {
      this._evaluateError(null);
      return;
    }

    if (id === this.participantId) {
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.participantsValidationApi +
      `/${id}`;

    this.http
      .get<ParticipantTokenValidationModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._validationError.bind(this)),
      )
      .subscribe(result => {
          if (result.participantIdentifier) {
            localStorage.setItem(Constants.PARTICIPANT_SELECTED_SURVEY, result.participantIdentifier);
          }

          if (destination) {
            localStorage.setItem(Constants.PARTICIPANT_REDIRECT_DESTINATION_KEY, destination);
          }

        if (result.tokenValidationErrorType === TokenValidationErrorTypes.noError && result.hasPassword) {
            this.oidcService.identity$.subscribe(identity => {
              if (identity) {
                this.oidcService.signOutRedirect();
              } else {
                this.oidcService.signInRedirect({
                  extraQueryParams: {
                    token: result.otac,
                    hasPassword: true,
                    acr_values: 'otac:' + result.otac
                  }
                });
              }
            });
            return;
          }

          if (result.tokenValidationErrorType === TokenValidationErrorTypes.noError) {
            this._validationStage$.next(ParticipantValidationStages.tokenValidated);
            this._participantValidation$.next(result);
            return;
          }

          this._evaluateError(result);
      });
  }

  setPassword(participantRegistrationModel: ParticipantRegistrationModel): void {
    if (
      !participantRegistrationModel ||
      !participantRegistrationModel.token ||
      !participantRegistrationModel.password ||
      !participantRegistrationModel.userAgreement
    ) {
      this._unexpectedError();

      return;
    }

    this._validationStage$.next(ParticipantValidationStages.settingPassword);

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.participantsValidationApi;

    this.http
      .post<SimpleStringResult>(url, participantRegistrationModel)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._setPasswordError.bind(this))
      )
      .subscribe((result: SimpleStringResult | null) => {
        if (result) {
          const otac = result.value;

          if (!otac) {
            this._unexpectedError();
            return;
          }

          this._validationStage$.next(ParticipantValidationStages.authenticating);
          localStorage.setItem(Constants.INITIAL_ACTIVATION_KEY, 'true');

          this.oidcService.signInRedirect({
            extraQueryParams: {
              acr_values: 'otac:' + otac
            }
          });
        }
      });
  }

  private _evaluateError(result: ParticipantTokenValidationModel | null): void {

    let errorCode: number = ErrorMessage.participantValidationInvalidTokenErrorCode;

    this._validationStage$.next(ParticipantValidationStages.tokenError);

    if (!result) {

      result = {
      } as ParticipantTokenValidationModel;

    } else {
      switch (result.tokenValidationErrorType) {
        case TokenValidationErrorTypes.invalidToken:
          break;
        case TokenValidationErrorTypes.tokenIsUsed:
          errorCode = ErrorMessage.participantValidationUsedTokenErrorCode;
          break;
        case TokenValidationErrorTypes.tokenIsExpired:
          errorCode = ErrorMessage.participantValidationExpiredTokenErrorCode;
          break;
        default: this._unexpectedError();
          errorCode = ErrorMessage.genericErrorCode;
          break;
      }
    }

    result.applicationError = ApplicationError.create(
      errorCode,
      'participantValidationService',
      false
    );
    result.applicationError.errorHeader = 'errorHeaderTokenError';

    this._participantValidation$.next(result);
  }

  private _unexpectedError() {
    this._validationStage$.next(ParticipantValidationStages.tokenError);
    this._participantValidation$.next({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'participantValidationUnexpectedError'
      }
    } as ParticipantTokenValidationModel);
  }

  private _setPasswordError(err: any): Observable<SimpleStringResult | null> {
    this._handleError(err);
    return of(null);
  }

  private _validationError(err: any) {
    return of({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'participantValidationUnexpectedError'
      }
    } as ParticipantTokenValidationModel);
  }

  private _handleError(err: any): void {
    this.appService.setLoading(false);

    let detailedMessage: string = '';
    let statusCode = -1;

    if (err && err instanceof HttpErrorResponse) {
      statusCode = err.status;
      detailedMessage += `<br/>${err.statusText}<br/>${err.message}`;
    }

    const error = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'participantValidationService',
      false,
      err,
      detailedMessage,
      undefined,
      statusCode
    );

    this.appService.setLastError(error);
  }
}
